import { MessageSenderType } from "./message";

export enum Language {
    EN = 'EN',
    DE = 'DE',
    NL = 'NL',
    PL = 'PL',
    RU = 'RU',
    HU = 'HU',
    FR = 'FR',
    RO = 'RO',
    ES = 'ES',
    IT = 'IT',
    CS = 'CS',
    BG = 'BG',
    HR = 'HR',
    TR = 'TR',
    PT = 'PT',
    LT = 'LT',
    LV = 'LV',
    EE = 'EE',
    SK = 'SK',
    SL = 'SL',
}

export enum QuestionType {
    ATTRIBUTION_SOURCE = "ATTRIBUTION_SOURCE",
    ATTRIBUTION_CODE = "ATTRIBUTION_CODE",
    LANGUAGE_SELECTION = "LANGUAGE_SELECTION",
    PROFESSION_SELECTION = "PROFESSION_SELECTION",
    CUSTOM_OCCUPATION_INPUT = "CUSTOM_OCCUPATION_INPUT",
    PROFESSION = 'PROFESSION',
    FINAL_PROFESSION_CONFIRMATION = "FINAL_PROFESSION_CONFIRMATION",
    PERSONAL = 'PERSONAL',
    CV_UPLOAD = 'CV_UPLOAD',
    CERT_COLLECTION = 'CERT_COLLECTION',
    VCA_UPLOAD = 'VCA_UPLOAD',
    ID_UPLOAD = 'ID_UPLOAD',
    HOME_ADDRESS = 'HOME_ADDRESS',
    ACTION = "ACTION",
    CLOSING = "CLOSING"
}

export enum CommunicationChannel {
    WHATSAPP = 'WHATSAPP',
    AI_BOT = 'AI_BOT',
    UNKNOWN = 'UNKNOWN'
}

// Dynamic profession type - any string value is allowed
// The actual professions are fetched from the backend
export type ProfessionType = string;

// Legacy enum values kept for reference
export const LEGACY_PROFESSION_TYPES = {
    TRUCK_MECHANIC: 'TRUCK_MECHANIC',
    CNC: 'CNC',
    ELECTRICIAN: 'ELECTRICIAN',
    IRON_WORKER: 'IRON_WORKER',
    WELDER: 'WELDER',
    PLUMBER: 'PLUMBER',
    MASON: 'MASON',
    PIPE_FITTER: 'PIPE_FITTER',
} as const; 

// Based on QuestionOptionDto from backend, assuming it's simple like this for now.
// If it's more complex, we might need to define it based on ../questions/questions.dto
export interface QuestionOption {
    key: string; 
    label: string; 
}

export interface Session {
    id: number; // Added based on SessionEntity, DTOs often omit DB id if not needed for client
    workerId: string;
    publicShareId: string;
    phoneNumber?: string | null; // Backend snapshots may explicitly report no phone number
    workerFirstName?: string | null; // Backend snapshots may explicitly omit the name
    workerLastName?: string | null;
    currentQuestionId?: number | null;
    language: Language; // Using the local Language enum
    professionKey?: ProfessionType | string | null; // Using local ProfessionType, allow string for flexibility if backend sends other values
    channel: CommunicationChannel; // Using local CommunicationChannel enum
    currentQuestionType?: QuestionType | null; // Using local QuestionType enum
    answers?: Record<string, any>; // Legacy snapshots may include this; current SessionDto does not
    aiPaused: boolean; // Added from SessionEntity, as it's relevant for UI
    adminIntervention: boolean; // Added from SessionEntity, as it's relevant for UI
    stateVersion: string;
    takeoverEpoch: string;
    createdAt: string; // Changed to string to match dummy data and typical API responses
    updatedAt: string; // Changed to string
    lastMessage?: string;
    lastMessageSender?: MessageSenderType;
    lastMessageAt?: string;
    lastInboundAt?: string | null; // Last INBOUND (worker) message — anchor for the 24h WhatsApp SLA window
    adminLastViewedAt?: string | null; // Timestamp when admin last viewed this session
    verificationAttempts?: number; // Number of verification attempts for current question
    verificationStartedAt?: string | null; // When verification was started
    aiBotStatus?: string | null; // Current AI bot status (e.g., 'AWAITING_VERIFICATION')
    temporaryAnswerData?: Record<string, any> | null; // Temporary answer data awaiting verification
    temporaryAiValidationFeedback?: string | null; // Last verification prompt sent to user
    isActive?: boolean;
    // options?: QuestionOption[]; // This was in SessionDto, but seems more related to a specific message/question context

    // Referral attribution. Set on the first inbound message via CTWA
    // metadata or a #REF tag in the body; the bot fallback question can
    // also stamp these later in the conversation, and an admin can pick a
    // candidate from the suggestion flow (MANUAL). `referralLinkId` is null
    // for organic / unattributed traffic; `attributionConfidence` is the
    // app-layer enum (DIRECT_AD | DIRECT | BOT_CAPTURED | MANUAL | NONE).
    referralLinkId?: string | null;
    attributionConfidence?: string | null;
    ctwaClid?: string | null;
    metaAdId?: string | null;
    metaCampaignId?: string | null;
}

// This mirrors the backend UpdateSessionDto, useful for sending updates
export interface UpdateSessionDto {
    currentQuestionId?: number | null;
    currentQuestionType?: QuestionType | null;
    language?: Language;
    professionKey?: ProfessionType | string | null;
    answers?: Record<string, any> | null;
    aiPaused?: boolean;
    adminIntervention?: boolean;
    aiBotStatus?: string | null;
    temporaryAnswerData?: Record<string, any> | null;
    temporaryAiValidationFeedback?: string | null;
}

// This mirrors the backend AdminMessageDto
export interface AdminMessageDto {
    message: string;
    clientRequestId: string;
}
